import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindContextKeyToSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts/selectors';
import { defineMessages, injectIntl } from 'react-intl';
import { withStyles } from '@lifeomic/chroma-react/styles';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _ from 'lodash';
import { contextKeys } from '../../../../../redux/modules/asyncRequestContexts';
import { surveysActions } from '../../../../../redux/modules/surveys';
import { asyncRequestContextsActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import moment from 'moment';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { ChevronRight } from '@lifeomic/chromicons';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import {
  Survey,
  SurveyAutopopulateContainer,
  FullPageContainer,
} from '@lifeomic/phc-web-toolkit/src/components/Surveys';
import { userSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/user';
import { groupActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/group';
import SendSubjectSurveyButton from './SendSubjectSurveyButton';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';

const messages = defineMessages({
  createSurveyResponseFailure: {
    id: 'patientViewer.internalSurveys.createSurveyResponse.failure',
    defaultMessage: 'Failed to create survey response',
  },
  createSurveyResponseSuccess: {
    id: 'patientViewer.internalSurveys.createSurveyResponse.success',
    defaultMessage: 'Successfully submitted survey response',
  },
  title: {
    id: 'patientViewer.internalSurveys.columns.title',
    defaultMessage: 'Title',
  },
  description: {
    id: 'patientViewer.internalSurveys.columns.description',
    defaultMessage: 'Description',
  },
  openTooltip: {
    id: 'subjectViewer.manageSurveys.openTooltip',
    defaultMessage: 'Begin survey',
  },
  date: {
    id: 'patientViewer.internalSurveys.columns.date',
    defaultMessage: 'Date',
  },
  viewSurvey: {
    id: 'patientViewer.internalSurveys.columns.viewSurvey',
    defaultMessage: 'View Survey',
  },
});

const createUserSurveyContextKey = contextKeys.patientViewer.createUserSurvey;
const groupsContextKey = contextKeys.patientViewer.fetchSurveyListGroups;

const {
  isFetchingSelector: isCreatingUserSurveySelector,
  responsesSelector: createdSurveyResponseSelector,
} = bindContextKeyToSelectors(createUserSurveyContextKey);

const { responsesSelector: groupsSelector } =
  bindContextKeyToSelectors(groupsContextKey);

const styles = theme => ({
  loadingContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  surveyContainer: {
    padding: theme.spacing(2),
  },
});

class SurveyList extends PureComponent {
  state = {
    focusedSurvey: null,
    focusedSurveyResponse: null,
    isCreated: null,
    isSaveAndExiting: false,
  };

  componentDidMount() {
    const {
      resetContext,
      intl: { formatMessage },
      fetchGroups,
    } = this.props;

    const params = { pageSize: 100 };
    fetchGroups({ params, contextKey: groupsContextKey });
  }

  getColumns = formatMessage => [
    {
      header: { label: formatMessage(messages.title) },
      cell: {
        content: survey => _.get(survey, 'title'),
      },
    },
    {
      header: { label: formatMessage(messages.description) },
      cell: {
        content: survey => _.get(survey, 'description'),
      },
    },
    {
      header: { label: formatMessage(messages.date) },
      cell: {
        content: survey => {
          const date = _.get(survey, 'date');
          if (!date) return '';

          return moment(date).format('LLL');
        },
      },
    },
  ];

  handleSurveyClick = async e => {
    const { resetContext } = this.props;

    const surveyId = _.get(e, 'currentTarget.dataset.surveyId');
    if (!surveyId) {
      console.error(
        'Survey Row was clicked but could not find the associated survey ID',
        e,
      );
      return;
    }

    const targetSurvey = this.props.projectSurveys.find(
      projectSurvey => _.get(projectSurvey, 'id') === surveyId,
    );

    if (!targetSurvey) {
      console.error(
        `The selected survey ID "${surveyId}" could not be found in the surveys returned from the API`,
      );
    }

    resetContext(createUserSurveyContextKey);

    this.setState({
      focusedSurvey: targetSurvey,
      focusedSurveyResponse: null,
      isCreated: null,
    });
  };

  handleSurveyResponseUpdate = updatedResponse => {
    this.setState({ focusedSurveyResponse: updatedResponse });
  };

  handleSurveyClose = () => {
    this.setState({
      focusedSurvey: null,
      focusedSurveyResponse: null,
      isCreated: null,
      isSaveAndExiting: false,
    });
  };

  handleSurveyComplete = async (isAutoSave, onSaveComplete) => {
    const {
      createSurveyResponse,
      updateSurveyResponse,
      patient,
      intl: { formatMessage },
      resetContext,
    } = this.props;

    const { focusedSurvey, focusedSurveyResponse } = this.state;

    const subjectId = patient.id;

    const upsertSurveyResponse = notification => {
      resetContext(createUserSurveyContextKey);

      if (this.state.isCreated) {
        focusedSurveyResponse.resourceType = 'QuestionnaireResponse';
        focusedSurveyResponse.subject = { reference: `Patient/${subjectId}` };
        focusedSurveyResponse.questionnaire = {
          reference: `Questionnaire/${focusedSurvey.id}`,
        };
        return updateSurveyResponse(
          focusedSurveyResponse,
          createUserSurveyContextKey,
          notification,
        );
      } else {
        // Set this to true immediately to avoid sending email notifications
        // more than once when the response is created
        this.setState({ isCreated: true });
        return createSurveyResponse(
          subjectId,
          focusedSurvey.id,
          focusedSurveyResponse,
          createUserSurveyContextKey,
          notification,
        );
      }
    };

    if (isAutoSave) {
      return upsertSurveyResponse().then(onSaveComplete);
    }

    const notification = new NotificationHelper(
      null,
      formatMessage(messages.createSurveyResponseFailure),
    );

    this.setState({ isSaveAndExiting: true });
    await upsertSurveyResponse(notification);
    this.handleSurveyClose();
  };

  getSubjectGroup = () =>
    this.props.groups.find((group = {}) => group.name === 'Subjects') || null;

  render() {
    const {
      patient,
      projectSurveys,
      isFetchingProjectSurveys,
      classes,
      user,
      isCreatingUserSurvey,
      intl: { formatMessage },
      resetResponses,
      mrnConfig,
    } = this.props;

    const { focusedSurvey, focusedSurveyResponse, isSaveAndExiting } =
      this.state;

    return (
      <>
        {!patient ||
        !!focusedSurvey ||
        isSaveAndExiting ||
        isCreatingUserSurvey ? (
          <div className={classes.loadingContainer}>
            <DotLoader />
          </div>
        ) : (
          <InfiniteScroll
            hasNextPage={this.props.hasMoreToLoad}
            loading={isFetchingProjectSurveys}
            loadMore={this.props.loadMode}
            scrollOffset={20}
            scrollContainer="parent"
          >
            <TableModule
              config={this.getColumns(formatMessage)}
              data={projectSurveys}
              wrapCellContent
              isLoading={isFetchingProjectSurveys}
              rowActions={survey => (
                <>
                  {survey?.id && (
                    <Tooltip title={formatMessage(messages.openTooltip)}>
                      <IconButton
                        data-survey-id={survey.id}
                        aria-label={formatMessage(messages.viewSurvey)}
                        color="inverse"
                        icon={ChevronRight}
                        onClick={e => {
                          e.stopPropagation();
                          e.currentTarget.blur();
                          this.handleSurveyClick(e);
                        }}
                      />
                    </Tooltip>
                  )}
                  <SendSubjectSurveyButton
                    survey={survey}
                    patient={this.props.patient}
                    subjectGroup={this.getSubjectGroup()}
                  />
                </>
              )}
            />
          </InfiniteScroll>
        )}
        <FullPageContainer
          title={_.get(focusedSurvey, 'title')}
          isOpen={!!focusedSurvey && !isSaveAndExiting}
          onClose={this.handleSurveyClose}
        >
          <div className={classes.surveyContainer}>
            <SurveyAutopopulateContainer
              questionnaire={focusedSurvey}
              patientId={patient.id}
              renderWithAutopopulateValues={autopopulateUpdates => (
                <Survey
                  patient={patient}
                  mrnConfig={mrnConfig}
                  isReadOnly={false}
                  questionnaire={focusedSurvey}
                  questionnaireResponse={focusedSurveyResponse}
                  onResponseUpdate={this.handleSurveyResponseUpdate}
                  onComplete={this.handleSurveyComplete}
                  nonSubjectAuthor={{
                    reference: user.id,
                    display: user.profile.name,
                  }}
                  onClose={this.handleSurveyClose}
                  autopopulateValues={autopopulateUpdates}
                  autoSave={true}
                  templatePreviewEnabled
                />
              )}
            />
          </div>
        </FullPageContainer>
      </>
    );
  }
}

SurveyList.propTypes = {
  patient: PropTypes.object.isRequired,
  isFetchingProjectSurveys: PropTypes.bool.isRequired,
  projectSurveys: PropTypes.array.isRequired,
};

const mapDispatchToProps = {
  resetContext: asyncRequestContextsActions.resetContext,
  fetchSurveys: surveysActions.fetchSurveys,
  createSurveyResponse: surveysActions.createSurveyResponse,
  updateSurveyResponse: surveysActions.updateSurveyResponse,
  fetchGroups: groupActions.fetchAccountGroups,
};

const mapStateToProps = state => ({
  isCreatingUserSurvey: isCreatingUserSurveySelector(state),
  createdUserSurveyResponse: createdSurveyResponseSelector(state)[0],
  groups: groupsSelector(state),
  user: userSelectors.selectActiveUser(state),
});

export default compose(
  injectIntl,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(SurveyList);
